'use client'

import { useEffect, useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { parseWelcomeCoupon, welcomeCouponLastDay, type WelcomeCoupon } from '@/lib/marketing/welcomeCouponWire'

/** Reintentos para leer el cupón si el sondeo de rescate le ganó al servidor. */
const REFETCH_DELAYS_MS = [2500, 6000] as const

type Props = {
  /** Cupón que ya trajo la respuesta que confirmó la orden, o null. */
  initial: WelcomeCoupon | null
  /** El cliente aceptó con oferta: si `initial` es null, vale la pena preguntar. */
  expect: boolean
  /** Token del intent: la llave para volver a preguntar a intent-status. */
  receiptToken: string | null
  accentColor: string
}

/**
 * Código de bienvenida en la confirmación online ("10 % en tu próximo pedido").
 *
 * El servidor acuña el cupón un instante DESPUÉS de escribir la orden, y el
 * sondeo de rescate de PaymentStep suele confirmar antes. Por eso, si el cliente
 * aceptó la oferta y el cupón no vino, se vuelve a preguntar dos veces a
 * intent-status (la misma llave que ya usa ese sondeo). Si no llega, no se
 * muestra nada: nunca un código inventado ni un "cargando" eterno.
 */
export function WelcomeCouponCard({ initial, expect, receiptToken, accentColor }: Props) {
  const t = useTranslations('public.onlineOrder.checkout')
  const locale = useLocale()
  const [coupon, setCoupon] = useState<WelcomeCoupon | null>(initial)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (coupon || !expect || !receiptToken) return
    let cancelled = false
    const timers = REFETCH_DELAYS_MS.map((ms) =>
      setTimeout(async () => {
        if (cancelled) return
        try {
          const res = await fetch(`/api/online-orders/intent-status?token=${encodeURIComponent(receiptToken)}`)
          if (!res.ok) return
          const found = parseWelcomeCoupon(await res.json())
          if (found && !cancelled) setCoupon(found)
        } catch {
          /* sin red: el siguiente intento, o nada */
        }
      }, ms),
    )
    return () => {
      cancelled = true
      timers.forEach(clearTimeout)
    }
  }, [coupon, expect, receiptToken])

  if (!coupon) return null

  async function copy() {
    if (!coupon) return
    try {
      await navigator.clipboard.writeText(coupon.code)
      setCopied(true)
    } catch {
      /* sin portapapeles: el código queda a la vista para copiarlo a mano */
    }
  }

  return (
    <div
      data-testid="welcome-coupon-card"
      className="mt-6 w-full rounded-2xl border-2 border-dashed bg-white px-4 py-3 text-left"
      style={{ borderColor: accentColor }}
    >
      <p className="text-sm font-bold text-gray-900">{t('welcomeCouponTitle', { pct: coupon.pct })}</p>
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="font-mono text-2xl font-black tracking-widest select-all" style={{ color: accentColor }}>
          {coupon.code}
        </span>
        <button
          type="button"
          onClick={copy}
          className="shrink-0 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700"
        >
          {copied ? t('welcomeCouponCopied') : t('welcomeCouponCopy')}
        </button>
      </div>
      <p className="mt-1 text-xs text-gray-500">
        {t('welcomeCouponHint', { date: welcomeCouponLastDay(coupon.endsAt, locale) })}
      </p>
      {/* "Mis descuentos" de la cuenta Redy: entra con su número (verificado por
          código) y ahí queda el cupón, asignado a ese teléfono. /cuenta vive en
          el dominio consumer; el proxy redirige desde useredy.com. */}
      <a
        href="/cuenta/entrar?next=descuentos"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-2 inline-block text-xs font-semibold underline"
        style={{ color: accentColor }}
      >
        {t('welcomeCouponAccount')}
      </a>
    </div>
  )
}
